/**
 * Shared auth store factory for product applications (Athena, Toucan)
 *
 * Creates a Zustand store for managing authentication state using
 * the ytest.ai identity service SSO flow.
 */
import { create } from 'zustand';
import { createAuthService } from './service';
export function createProductAuthStore(config) {
    const authService = createAuthService(config);
    // Cross-tab logout channel. Origin-scoped, so athena/toucan on different
    // subdomains do not see each other's events. Null in non-browser envs.
    const authChannel = typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel('ytest-auth')
        : null;
    const store = create((set) => ({
        user: authService.getUser(),
        tenantSlug: authService.getTenantSlug(),
        // No persisted getter for the full tenant — checkAuth() fills it
        // from /me on every boot. Until that resolves, consumers see null.
        tenant: null,
        isAuthenticated: authService.isAuthenticated(),
        isLoading: true, // Start with loading true until auth is validated
        error: null,
        /**
         * Initialize auth from URL token (SSO redirect from identity service)
         * Returns true if token was found and stored
         */
        initFromToken: () => {
            const token = authService.initFromUrlToken();
            if (token) {
                set({ isAuthenticated: true });
                return true;
            }
            return false;
        },
        logout: () => {
            authService.logout();
            set({
                user: null,
                tenantSlug: null,
                tenant: null,
                isAuthenticated: false,
                error: null,
            });
            authChannel?.postMessage({ type: 'logout' });
        },
        checkAuth: async () => {
            set({ isLoading: true });
            // Always try to validate with identity service
            // This will check for valid cookie (SSO)
            const authData = await authService.validateToken();
            if (authData) {
                set({
                    user: authData.user,
                    tenantSlug: authData.tenant.slug,
                    tenant: authData.tenant,
                    isAuthenticated: true,
                    isLoading: false,
                });
            }
            else {
                set({
                    user: null,
                    tenantSlug: null,
                    tenant: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
            }
        },
        clearError: () => {
            set({ error: null });
        },
        setUser: (user, tenantSlug) => {
            authService.setUser(user);
            authService.setTenantSlug(tenantSlug);
            set({
                user,
                tenantSlug,
                isAuthenticated: true,
                error: null,
            });
        },
    }));
    // Receive logout signals from other tabs of this origin and mirror them
    // locally. We clear state, then hard-navigate to the marketing /logout
    // route — same target as the manual logout button (Layout.tsx). A bare
    // setState is not enough: the ProtectedRoute checks auth once on mount
    // and uses local component state, so it does not re-render when the
    // store's isAuthenticated flips. The hard navigation forces a fresh
    // mount which then redirects to /login.
    if (typeof window !== 'undefined') {
        const ytestUrl = config?.ytestUrl || 'https://ytest.ai';
        const clearAndRedirect = () => {
            store.setState({
                user: null,
                tenantSlug: null,
                tenant: null,
                isAuthenticated: false,
                error: null,
            });
            window.location.href = `${ytestUrl}/logout`;
        };
        authChannel?.addEventListener('message', (e) => {
            if (e.data?.type === 'logout')
                clearAndRedirect();
        });
        // Storage-event fallback for browsers without BroadcastChannel.
        // Fires only from other tabs; never echoes to the originating tab.
        window.addEventListener('storage', (e) => {
            if (e.key === 'access_token' && e.newValue === null)
                clearAndRedirect();
        });
    }
    return store;
}
//# sourceMappingURL=productAuthStore.js.map